import type { InputFrame } from "../input";
import { surfaceAt } from "../level";
import type { Skater } from "../skater";
import { vehicleLocal, vehiclePose, type Vehicle, type VehiclePose } from "../vehicles";
import { bail } from "./bail";
import { popOllie } from "./ground";
import { wrapAngle } from "./math";
import type { SkitchState } from "./state";
import {
  FULL_CHARGE,
  MAX_SPEED,
  REVERT_LINK_WINDOW,
  SKITCH_BOOST,
  SKITCH_CORNER,
  SKITCH_FACING,
  SKITCH_GAP,
  SKITCH_GRACE,
  SKITCH_MIN_SPEED,
  SKITCH_REACH,
  SKITCH_SIDE,
  SKITCH_STICK,
  SKITCH_SWAY,
  SKITCH_VEER,
  SKITCH_WOBBLE,
  VEHICLE_HIT_MARGIN,
} from "./tuning";

// Skitching (#37, THPS 4): roll up behind a moving vehicle and hold up on
// the stick to grab its back end. It tows you at its speed while you keep a
// left / right needle steady (its turns throw the needle outward), and the
// "Skitch" entry scores for as long as you hang on. Down on the stick lets
// go, or crouch and let go of ollie to pop off: either way you shoot off
// faster than it, veering to one side so you clear it. The combo carries
// on through a skitch like a manual; let go onto the ground and it banks
// unless you manual in time. Anything else that gets in a vehicle's way is
// knocked down.

export const SKITCH_TRICK = { name: "Skitch", points: 100, perSecond: 120 } as const;

/** Where a vehicle tows you from: `SKITCH_GAP` behind its back end, swayed to its right by `side`. */
function towPoint(v: Vehicle, pose: VehiclePose, side: number): { x: number; z: number } {
  const fx = Math.sin(pose.yaw);
  const fz = Math.cos(pose.yaw);
  const back = v.hl + SKITCH_GAP;
  return { x: pose.x - fx * back - fz * side, z: pose.z - fz * back + fx * side };
}

/** Up on the stick right behind a vehicle, rolling its way: grab on. True if it did. */
export function trySkitch(sk: Skater, input: InputFrame): boolean {
  const vehicles = sk.level.vehicles;
  if (!vehicles || input.moveY < SKITCH_STICK || sk.manual) return false;
  if (Math.abs(sk.speed) < SKITCH_MIN_SPEED) return false;
  const fakie = sk.speed < 0;
  const travel = sk.yaw + (fakie ? Math.PI : 0);
  for (const [i, v] of vehicles.entries()) {
    const pose = vehiclePose(v, sk.clock);
    if (Math.cos(wrapAngle(travel - pose.yaw)) < SKITCH_FACING) continue;
    const { along, side } = vehicleLocal(pose, sk.x, sk.z);
    if (along > -v.hl || along < -v.hl - SKITCH_REACH) continue;
    if (Math.abs(side) > v.hw + SKITCH_SIDE) continue;
    startSkitch(sk, i, fakie);
    return true;
  }
  return false;
}

function startSkitch(sk: Skater, vehicle: number, fakie: boolean): void {
  sk.landing = null;
  sk.revertWindow = 0;
  sk.linkWindow = 0;
  sk.manualBuffer = 0;
  sk.charge = 0;
  sk.skitch = {
    vehicle,
    index: sk.addTrick(SKITCH_TRICK.name, SKITCH_TRICK.points),
    balance: sk.balanceCarry ?? (sk.rng() - 0.5) * 0.3,
    balanceVel: 0,
    time: 0,
    fakie,
  };
  follow(sk, sk.skitch);
}

/** Puts the skater at the tow point, facing the vehicle's way (fakie if it grabbed on fakie). */
function follow(sk: Skater, s: SkitchState): VehiclePose {
  const v = sk.level.vehicles![s.vehicle]!;
  const pose = vehiclePose(v, sk.clock);
  const p = towPoint(v, pose, s.balance * SKITCH_SWAY);
  sk.x = p.x;
  sk.z = p.z;
  const ground = surfaceAt(sk.level, p.x, p.z);
  sk.y = ground.h;
  sk.setNormal(ground);
  sk.yaw = pose.yaw + (s.fakie ? Math.PI : 0);
  sk.speed = s.fakie ? -v.speed : v.speed;
  return pose;
}

/** One step being towed. Called from the ground step while `sk.skitch` is set. */
export function stepSkitch(sk: Skater, s: SkitchState, input: InputFrame, dt: number): void {
  const v = sk.level.vehicles![s.vehicle]!;
  s.time += dt;
  sk.balanceTime += dt;
  sk.manualBuffer = 0;
  sk.combo.addPoints(s.index, SKITCH_TRICK.perSecond * dt);
  // A turn throws the needle to the outside: every loop turns left, so right.
  s.balanceVel += SKITCH_CORNER * v.speed * v.speed * vehiclePose(v, sk.clock).curve * dt;
  if (input.held.ollie) sk.charge += dt;
  sk.crouch += ((input.held.ollie ? 1 : 0.5) - sk.crouch) * Math.min(1, dt * 14);
  if (sk.stepBalance(s, input.moveX, dt, SKITCH_WOBBLE * sk.wobble("grind"))) {
    follow(sk, s);
    sk.skitch = null;
    bail(sk, sk.out);
    return;
  }
  follow(sk, s);
  if (input.released.ollie) {
    letGo(sk, s, input);
    const f = sk.forward;
    popOllie(sk, f, 0);
    return;
  }
  if (input.moveY < -SKITCH_STICK) {
    letGo(sk, s, input);
    sk.charge = 0;
    // The combo waits a moment for a manual, then banks.
    sk.linkWindow = REVERT_LINK_WINDOW;
  }
}

/** Lets go: off to one side (the stick's, else the way the needle leans, else right), faster than the vehicle. */
function letGo(sk: Skater, s: SkitchState, input: InputFrame): void {
  const v = sk.level.vehicles![s.vehicle]!;
  sk.skitch = null;
  sk.balanceCarry = s.balance;
  const right = Math.abs(input.moveX) > 0.3 ? input.moveX > 0 : s.balance !== 0 ? s.balance > 0 : true;
  // Right is yaw going down (see the ground step's steering).
  sk.yaw += right ? -SKITCH_VEER : SKITCH_VEER;
  const mag = Math.min(MAX_SPEED * sk.special.boost * sk.scale("speed"), v.speed + SKITCH_BOOST);
  sk.speed = s.fakie ? -mag : mag;
  sk.charge = Math.min(sk.charge, FULL_CHARGE);
  sk.skitchGrace = { vehicle: s.vehicle, t: SKITCH_GRACE };
}

/** A vehicle drives into you (or you ride into it): knocked down. Not while towed, nor by the one you just let go of. */
export function stepVehicleHits(sk: Skater, dt: number): void {
  const vehicles = sk.level.vehicles;
  if (!vehicles) return;
  const grace = sk.skitchGrace;
  if (grace) {
    grace.t -= dt;
    if (grace.t <= 0) sk.skitchGrace = null;
  }
  if (sk.skitch || (sk.mode !== "ground" && sk.mode !== "air" && sk.mode !== "foot")) return;
  for (const [i, v] of vehicles.entries()) {
    if (sk.skitchGrace?.vehicle === i) continue;
    const pose = vehiclePose(v, sk.clock);
    const { along, side } = vehicleLocal(pose, sk.x, sk.z);
    if (Math.abs(along) > v.hl + VEHICLE_HIT_MARGIN || Math.abs(side) > v.hw + VEHICLE_HIT_MARGIN) continue;
    if (sk.y - surfaceAt(sk.level, pose.x, pose.z).h > v.h) continue;
    bail(sk, sk.out);
    // Shoved along the way it's driving.
    sk.vx = Math.sin(pose.yaw) * v.speed;
    sk.vz = Math.cos(pose.yaw) * v.speed;
    return;
  }
}
